import React, { createContext, useContext, ReactNode } from 'react';
import { StorageService } from './StorageInterface';
import { LocalStorageAdapter } from './LocalStorageAdapter';

// Create a context for the storage service
const StorageServiceContext = createContext<StorageService | null>(null);

// Default storage service implementation
const defaultStorageService = new LocalStorageAdapter();

interface StorageServiceProviderProps {
  children: ReactNode;
  storageService?: StorageService;
}

/**
 * Provider component for the storage service
 * Allows injecting a custom storage service for testing or different environments
 */
export function StorageServiceProvider({ 
  children, 
  storageService = defaultStorageService 
}: StorageServiceProviderProps) {
  return (
    <StorageServiceContext.Provider value={storageService}>
      {children}
    </StorageServiceContext.Provider>
  );
}

/**
 * Hook to access the storage service
 * This follows the Dependency Inversion Principle by allowing components
 * to depend on the StorageService interface rather than concrete implementations
 */
export function useStorageService(): StorageService {
  const context = useContext(StorageServiceContext);
  
  if (context === null) {
    throw new Error('useStorageService must be used within a StorageServiceProvider');
  }
  
  return context;
}
